// ---- Audio (procedural WebAudio synth — no asset files, stays single-file) ----
export const Audio = (() => {
  let ac = null, master = null, musicGain = null, muted = false;
  let musicTimer = 0, musicStep = 0;
  function ensure() {
    if (ac) return;
    try {
      ac = new (window.AudioContext || /** @type {any} */ (window).webkitAudioContext)();
      master = ac.createGain(); master.gain.value = 0.5; master.connect(ac.destination);
      musicGain = ac.createGain(); musicGain.gain.value = 0.16; musicGain.connect(master);
    } catch (e) { ac = null; }
  }
  function resume() { ensure(); if (ac && ac.state === 'suspended') ac.resume(); }
  // one-shot tone with an exponential pitch slide + AD envelope
  function tone(freq, dur, type, vol, slideTo, dest) {
    if (!ac || muted) return;
    const t = ac.currentTime;
    const o = ac.createOscillator(), g = ac.createGain();
    o.type = type || 'square';
    o.frequency.setValueAtTime(freq, t);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol || 0.3, t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g); g.connect(dest || master);
    o.start(t); o.stop(t + dur + 0.02);
  }
  // MEMORY FIX (0.19): noise() used to build a FRESH AudioBuffer per call — ~22KB of sample memory
  // allocated on every kill/hurt/boss/death sound. At deep-wave kill rates that's gigabytes of churn
  // per hour, and long sessions sat on multi-GB renderer heaps between collections (player report:
  // "3GB after some time"). Three 1s noise buffers are now rendered ONCE and shared; the per-call
  // fade that was baked into each buffer is a gain envelope instead, and sources get a hard stop().
  let noiseBufs = null;
  function ensureNoise() {
    if (noiseBufs || !ac) return;
    noiseBufs = [];
    for (let b = 0; b < 3; b++) {
      const n = Math.floor(ac.sampleRate * 1.0);
      const buf = ac.createBuffer(1, n, ac.sampleRate), d = buf.getChannelData(0);
      for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
      noiseBufs.push(buf);
    }
  }
  function noise(dur, vol, freq) {
    if (!ac || muted) return;
    ensureNoise();
    const t = ac.currentTime;
    const src = ac.createBufferSource(); src.buffer = noiseBufs[(Math.random() * noiseBufs.length) | 0];
    const f = ac.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = freq || 800;
    const g = ac.createGain();
    g.gain.setValueAtTime(vol || 0.3, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);   // the decay the old buffers baked in
    src.connect(f); f.connect(g); g.connect(master);
    src.start(t); src.stop(t + dur + 0.02);
  }
  const sounds = {
    shoot:  () => tone(520, 0.06, 'square', 0.05, 360),
    hit:    () => tone(240, 0.05, 'sawtooth', 0.05, 150),
    coin:   (p) => tone(680 + (p || 0) * 36, 0.06, 'triangle', 0.10, 920 + (p || 0) * 60),
    chain:  (p) => tone(440 + (p || 0) * 26, 0.10, 'square', 0.13, 680 + (p || 0) * 40),
    kill:   () => { tone(180, 0.12, 'sawtooth', 0.13, 60); noise(0.12, 0.10, 500); },
    hurt:   () => { tone(150, 0.22, 'sawtooth', 0.26, 50); noise(0.18, 0.16, 300); },
    dash:   () => tone(300, 0.16, 'sine', 0.16, 760),
    boss:   () => { tone(70, 0.7, 'sawtooth', 0.30, 46); noise(0.5, 0.18, 200); },
    shrine: () => { tone(520, 0.12, 'triangle', 0.18, 780); tone(780, 0.16, 'triangle', 0.14, 1180); },
    click:  () => tone(440, 0.04, 'square', 0.10, 520),
    death:  () => { tone(220, 0.9, 'sawtooth', 0.30, 40); noise(0.8, 0.20, 220); },
    cascade:() => { tone(120, 0.5, 'sawtooth', 0.30, 720); noise(0.4, 0.22, 600); },
    powerup:() => { tone(330, 0.10, 'triangle', 0.18, 495); tone(495, 0.12, 'triangle', 0.16, 660); tone(660, 0.14, 'triangle', 0.14, 880); },
  };
  // sparse procedural pad — a slow minor pentatonic arp, tempo rises with intensity
  const SCALE = [0, 3, 5, 7, 10], ROOT = 110;
  function music(intensity) {
    if (!ac || muted) return;
    if (--musicTimer > 0) return;
    musicTimer = Math.max(13, 30 - intensity * 2);
    const deg = SCALE[musicStep % SCALE.length];
    const oct = 1 + ((musicStep / SCALE.length | 0) % 2);
    const f = ROOT * oct * Math.pow(2, deg / 12);
    tone(f, 0.5, 'sine', 0.12, f, musicGain);
    if (musicStep % 4 === 0) tone(ROOT * 0.5, 0.6, 'triangle', 0.10, ROOT * 0.5, musicGain);
    musicStep++;
  }
  return {
    resume, music,
    play(name, arg) { ensure(); if (sounds[name]) sounds[name](arg); },
    toggle() { muted = !muted; return muted; },
    isMuted() { return muted; },
  };
})();
